import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Hide,
  HStack,
  IconButton,
  Stack,
  type StackProps,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { PlanningDailyViewWhereColumn } from "@src/__generated__/urql-graphql";
import { useResourcePlanningDailyViewStore } from "@src/components/modules/resource-planning/timeline/ResourcePlanningDayPage";
import { Select } from "@src/components/ui-kit";
import { Dash } from "@src/components/ui-kit/Dash";
import { DatePicker } from "@src/components/ui-kit/datePicker";
import { Icon } from "@src/components/ui-kit/Icon";
import { FiltersPanelRoot } from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { FiltersPanelSearch } from "@src/utils/components/filters/components/filters-panel-search";
import { toApiDate } from "@src/utils/dates";
import { useStore, useUpdateURI } from "@src/utils/hooks";
import { getParamFromQuery } from "@src/utils/url";
import { PlanningSettingsPopover } from "@src/widgets/PlanningSettingsPopover/PlanningSettingsPopover";
import { addDays, format, isThisWeek, subDays } from "date-fns";
import { isEmpty } from "lodash";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import { type FC, useEffect } from "react";

type ResourcePlanningFilterRowProps = { hideTeamFilter?: boolean } & StackProps;

export const ResourcePlanningFilterRow: FC<ResourcePlanningFilterRowProps> =
  observer(function ResourcePlanningFilterRow({ hideTeamFilter, ...props }) {
    const { resourcePlanningDayStore } = useStore();
    const resourcePlanningStore = useResourcePlanningDailyViewStore();
    const { query } = useRouter();
    const [startOfPeriod, endOfPeriod] =
      resourcePlanningStore.selectedDateRange;

    const teamFilter = resourcePlanningStore.where.filtersByColumn.get(
      PlanningDailyViewWhereColumn.TeamId,
    );

    const isThisPeriod = !isThisWeek(startOfPeriod);

    const goNextSevenDays = () => {
      resourcePlanningStore.selectedDate = addDays(endOfPeriod, 1);
    };

    const goPrevSevenDays = () => {
      resourcePlanningStore.selectedDate = subDays(startOfPeriod, 7);
    };

    const goToToday = () => {
      resourcePlanningStore.selectedDate = new Date();
    };

    useEffect(() => {
      runInAction(() => {
        resourcePlanningStore.searchTerm =
          getParamFromQuery("search", query) ?? "";
        const savedTeamFilter = localStorage.getItem("planning_team_filter");
        resourcePlanningStore.where.fromURLSearchParam(query);
        if (isEmpty(teamFilter?.value) && savedTeamFilter && !hideTeamFilter) {
          teamFilter?.setValue(JSON.parse(savedTeamFilter));
        }

        const from = query.from;

        if (!from?.length || Array.isArray(from)) return;

        resourcePlanningStore.selectedDate = new Date(from);
      });
    }, []);

    useUpdateURI({
      search: resourcePlanningStore.searchParams.search,
      where: resourcePlanningStore.searchParams.where ?? "",
      from: toApiDate(startOfPeriod),
      to: toApiDate(endOfPeriod),
    });

    const hasTeamFilter = !!teamFilter && !hideTeamFilter;

    return (
      <Stack
        pos="sticky"
        zIndex={30}
        top="0"
        align="flex-start"
        py="4"
        bg="white"
      >
        <FiltersPanelRoot filters={resourcePlanningStore.where}>
          <Grid
            alignItems="center"
            gap="4"
            templateRows={{
              base: "repeat(2, max-content)",
              lg: "max-content",
            }}
            templateColumns={{
              base: "repeat(5, minmax(0, 1fr))",
              lg: `repeat(${hasTeamFilter ? "2" : "1"}, max-content) 1fr repeat(3, max-content)`,
            }}
            w="full"
            px="4"
            {...props}
          >
            <GridItem
              gridColumn={{
                base: !hasTeamFilter ? "1 / span 5" : "1 / span 3",
                lg: "auto",
              }}
            >
              <FiltersPanelSearch
                searchPlaceholder={t`Search by person, position or team`}
                defaultValue={resourcePlanningStore.searchTerm}
                onSearch={(term) => {
                  resourcePlanningStore.searchTerm = term;
                  resourcePlanningDayStore.reset();
                  resourcePlanningStore.fetchDailyView();
                }}
              />
            </GridItem>

            {hasTeamFilter && (
              <GridItem
                gridColumn={{
                  base: "4 / span 2",
                  lg: "auto",
                }}
              >
                <Box
                  minW={{
                    base: undefined,
                    lg: "180px",
                  }}
                >
                  <Select
                    isMulti
                    onChange={(val) => {
                      teamFilter.setValue(val);
                      localStorage.setItem(
                        "planning_team_filter",
                        JSON.stringify(val),
                      );
                    }}
                    options={teamFilter.options}
                    placeholder={t`Select team`}
                    value={teamFilter.value}
                  />
                </Box>
              </GridItem>
            )}
            <GridItem as={Flex} alignItems="center">
              <FiltersPanelFilters
                innerMenuPlacement="right-start"
                clearButtonSide="right"
              />
            </GridItem>

            <Hide below="lg">
              <GridItem>
                {isThisPeriod && (
                  <Button
                    fontWeight="normal"
                    onClick={goToToday}
                    variant="ghost"
                  >
                    <Trans>Return to today</Trans>
                  </Button>
                )}
              </GridItem>
            </Hide>
            <GridItem>
              <PlanningSettingsPopover />
            </GridItem>
            <GridItem
              justifySelf="end"
              colSpan={{
                base: 3,
                lg: 1,
              }}
            >
              <HStack spacing="0">
                <IconButton
                  aria-label={t`prev`}
                  colorScheme="grey"
                  icon={<Icon name="chevron-left" />}
                  onClick={goPrevSevenDays}
                  roundedRight="none"
                  size={"sm"}
                  variant="outline"
                />
                <DatePicker
                  usePortal
                  trigger={
                    <Button
                      borderX="none"
                      colorScheme="grey"
                      leftIcon={
                        <Icon name="calendar" w="5" h="5" color="grey.300" />
                      }
                      rounded="none"
                      size="sm"
                      variant="outline"
                    >
                      {format(startOfPeriod, "MMM d")}
                      <Dash color="grey.400" />
                      {format(endOfPeriod, "MMM d")}
                    </Button>
                  }
                  onChange={(date) => {
                    if (!date) return;
                    resourcePlanningStore.selectedDate = date.start;
                  }}
                  selected={resourcePlanningStore.selectedDate}
                />
                <IconButton
                  aria-label={t`next`}
                  colorScheme="grey"
                  icon={<Icon name="chevron-right" />}
                  onClick={goNextSevenDays}
                  roundedLeft="none"
                  size="sm"
                  variant="outline"
                />
              </HStack>
            </GridItem>
          </Grid>
        </FiltersPanelRoot>
      </Stack>
    );
  });
